import DraggablePanel from '@/components/Common/DraggablePanel'

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h4 className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
    </label>
  )
}

function NumberInput({ label, value, min, max, step = 1, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
      <input
        type="number" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-16 px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-right"
      />
    </div>
  )
}

function ColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
      <div className="relative w-6 h-6 rounded overflow-hidden border border-slate-300 dark:border-gray-600">
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          className="absolute -top-1 -left-1 w-8 h-8 p-0 border-0 cursor-pointer" />
      </div>
    </div>
  )
}

function SelectInput({ label, value, options, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-28 px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-right"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

export default function CanalesSRPanel({ config, onChange, onClose }) {
  const upd = (key, value) => {
    onChange(prev => ({ ...prev, [key]: value }))
  }

  const styleOptions = [
    { value: 'solid', label: 'Sólido' },
    { value: 'dashed', label: 'Discontinuo' },
    { value: 'dotted', label: 'Punteado' },
  ]

  return (
    <DraggablePanel
      id="canales_sr"
      title="Canales S/R"
      width="w-72"
      defaultPosition={{ x: 16, y: 120 }}
      onClose={onClose}
    >
      <div className="p-3">
        <Section title="A) Principal">
          <NumberInput label="Pivot len" value={config.pivotLen} min={2} max={50} onChange={v => upd('pivotLen', v)} />
          <NumberInput label="ATR len" value={config.atrLen} min={5} max={100} onChange={v => upd('atrLen', v)} />
          <NumberInput label="Min channel bars" value={config.minChannelBars} min={3} max={200} onChange={v => upd('minChannelBars', v)} />
          <NumberInput label="Max channel bars" value={config.maxChannelBars} min={30} max={2000} onChange={v => upd('maxChannelBars', v)} />
          <NumberInput label="Channel quality" value={config.channelQuality} min={0.2} max={1} step={0.01} onChange={v => upd('channelQuality', v)} />
        </Section>

        <Section title="B) Canales">
          <Toggle label="Mostrar ascendentes" checked={config.showUp} onChange={v => upd('showUp', v)} />
          <Toggle label="Mostrar descendentes" checked={config.showDown} onChange={v => upd('showDown', v)} />
        </Section>

        <Section title="C) Estilo">
          <NumberInput label="Base width" value={config.baseWidth} min={1} max={5} onChange={v => upd('baseWidth', v)} />
          <SelectInput label="Base style" value={config.baseStyle} options={styleOptions} onChange={v => upd('baseStyle', v)} />
          <NumberInput label="Parallel width" value={config.parWidth} min={1} max={5} onChange={v => upd('parWidth', v)} />
          <SelectInput label="Parallel style" value={config.parStyle} options={styleOptions} onChange={v => upd('parStyle', v)} />
          <NumberInput label="Mid width" value={config.midWidth} min={1} max={3} onChange={v => upd('midWidth', v)} />
          <SelectInput label="Mid style" value={config.midStyle} options={styleOptions} onChange={v => upd('midStyle', v)} />
        </Section>

        <Section title="D) Visual">
          <Toggle label="Mostrar línea media" checked={config.showMidline} onChange={v => upd('showMidline', v)} />
          <Toggle label="Mostrar relleno" checked={config.showFill} onChange={v => upd('showFill', v)} />
          <Toggle label="Mostrar watermark" checked={config.showWatermark} onChange={v => upd('showWatermark', v)} />
          <SelectInput
            label="Extender"
            value={config.extend}
            options={[{ value: 'none', label: 'Ninguno' }, { value: 'right', label: 'Derecha' }, { value: 'both', label: 'Ambos' }]}
            onChange={v => upd('extend', v)}
          />
          <Toggle label="Borrar dibujos previos" checked={config.deletePrev} onChange={v => upd('deletePrev', v)} />
          <Toggle label="Mostrar etiquetas breakout" checked={config.showBreakoutLbl} onChange={v => upd('showBreakoutLbl', v)} />
        </Section>

        <Section title="E) Colores">
          <ColorInput label="Alcista" value={config.bullColor} onChange={v => upd('bullColor', v)} />
          <ColorInput label="Bajista" value={config.bearColor} onChange={v => upd('bearColor', v)} />
          <NumberInput label="Fill transp." value={config.fillTransp} min={80} max={99} onChange={v => upd('fillTransp', v)} />
        </Section>

        {/* WP-3 fase B: fuera «Alerta Buy/Sell / breakout / reacción» y
            «Formato webhook»: nadie los leía (ni el chart ni el backend);
            Canales S/R no tiene motor de alertas. */}
        <Section title="F) Dashboard">
          <Toggle label="Mostrar dashboard" checked={config.showDash} onChange={v => upd('showDash', v)} />
          <SelectInput
            label="Posición"
            value={config.dashPos}
            options={[
              { value: 'top_left', label: 'Sup. izq.' },
              { value: 'top_right', label: 'Sup. der.' },
              { value: 'bottom_left', label: 'Inf. izq.' },
              { value: 'bottom_right', label: 'Inf. der.' },
            ]}
            onChange={v => upd('dashPos', v)}
          />
        </Section>
      </div>
    </DraggablePanel>
  )
}
